import { Capacitor } from "@capacitor/core";
import { toError } from "@clipboard-health/util-ts";

import { APP_V2_APP_EVENTS, logError } from "../lib/analytics";

/**
 * Browser open options matching Capacitor's OpenOptions interface.
 * Re-exported here to avoid consumers needing to import from @capacitor/browser.
 */
export interface BrowserOpenOptions {
  /**
   * The URL to open in the browser
   */
  url: string;
  /**
   * Web only: Optional target for the browser open.
   * @default "_blank"
   */
  windowName?: string;
  /**
   * iOS only: The presentation style of the browser.
   * @default "popover"
   */
  presentationStyle?: "fullscreen" | "popover";
  /**
   * iOS only: The width of the browser when using presentationStyle 'popover' on iPads.
   */
  width?: number;
  /**
   * iOS only: The height of the browser when using presentationStyle 'popover' on iPads.
   */
  height?: number;
  /**
   * Android only: Show the toolbar at the top of the browser.
   */
  toolbarColor?: string;
}

export type BrowserEventType = "browserFinished" | "browserPageLoaded";

/**
 * Strips query parameters and fragments from a URL to avoid logging sensitive data.
 */
function redactUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    // If URL parsing fails, return a safe placeholder
    return "[invalid-url]";
  }
}

/**
 * Opens a URL in the device's browser.
 * On native platforms, opens in an in-app browser.
 * On web, opens in a new tab.
 *
 * @param options - Browser open options including the URL to open
 * @param source - Optional source identifier for logging purposes
 */
export async function openInBrowser(options: BrowserOpenOptions, source?: string): Promise<void> {
  const { url, windowName = "_blank", presentationStyle = "popover", ...rest } = options;

  if (Capacitor.isNativePlatform()) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({
        url,
        windowName,
        presentationStyle,
        ...rest,
      });
    } catch (maybeError: unknown) {
      logError(APP_V2_APP_EVENTS.OPEN_IN_BROWSER_FAILED, {
        error: toError(maybeError),
        metadata: {
          url: redactUrl(url),
          source,
          platform: "native",
        },
      });
      throw maybeError;
    }
  } else {
    window.open(url, windowName, "noopener,noreferrer");
  }
}

/**
 * Opens a URL in the device's browser with simple API.
 * Convenience wrapper around openInBrowser for simple URL opening.
 *
 * @param url - The URL to open
 * @param source - Optional source identifier for logging purposes
 */
export async function openUrlInBrowser(url: string, source?: string): Promise<void> {
  await openInBrowser({ url }, source);
}

/**
 * Adds a listener for browser events.
 * On native platforms, listens for in-app browser events.
 * On web, returns a no-op listener handle.
 *
 * @param eventType - The event type to listen for ("browserFinished" or "browserPageLoaded")
 * @param callback - The callback to invoke when the event fires
 * @returns A handle that can be used to remove the listener
 */
export async function addBrowserListener(eventType: BrowserEventType, callback: () => void) {
  if (!Capacitor.isNativePlatform()) {
    // Return a no-op handle for web
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  const { Browser } = await import("@capacitor/browser");
  if (eventType === "browserFinished") {
    return await Browser.addListener("browserFinished", callback);
  }
  return await Browser.addListener("browserPageLoaded", callback);
}
